/**
 * Last-resort readable copy for a translation key that could not be resolved.
 *
 * Translations are HTTP-loaded from the backend, so a missing/failed namespace
 * makes i18next return the KEY PATH — and users saw `fields.email_address` on
 * the login screen during a backend blip. A technical key must never reach the
 * user: this humanizer turns the key's leaf into readable Title Case words.
 *
 * It is wired as i18next's `parseMissingKeyHandler` (see ./config.ts), which
 * only fires for keys that genuinely resolved to nothing, so a real translation
 * is never touched.
 */

/** i18next plural suffixes — noise once the key is rendered as copy. */
const PLURAL_SUFFIX_PATTERN = /_(zero|one|two|few|many|other)$/u

/**
 * `auth:fields.email_address` → `Email Address`
 * `page.loginTitle`           → `Login Title`
 */
export const humanizeTranslationKey = (rawKey: string): string => {
  if (typeof rawKey !== 'string' || rawKey.length === 0) return ''

  // Only the leaf carries meaning: drop the `ns:` prefix and the parent path.
  const leaf = rawKey.split(':').pop() ?? ''
  const words = (leaf.split('.').pop() ?? '')
    .replace(PLURAL_SUFFIX_PATTERN, '')
    // camelCase / PascalCase → word boundaries, then snake_case / kebab-case.
    .replace(/([a-z0-9])([A-Z])/gu, '$1 $2')
    .replace(/[_-]+/gu, ' ')
    .trim()
    .split(/\s+/u)
    .filter((word) => word.length > 0)

  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

/**
 * i18next `parseMissingKeyHandler` signature. An explicit `defaultValue` passed
 * at the call site (`t(key, 'English copy')`) always wins — humanizing is only
 * for keys with no copy at all.
 */
export const resolveMissingTranslationKey = (key: string, defaultValue?: string): string => {
  // E2E journeys install this sink (tests/e2e/journeys/support/gate.ts) and fail on
  // every key that reached a user without copy; absent everywhere else.
  const sink = (globalThis as { __batMissingKeys?: string[] }).__batMissingKeys
  if (defaultValue === undefined && Array.isArray(sink)) sink.push(key)
  return defaultValue ?? humanizeTranslationKey(key)
}
